import { useEffect, useRef, useState } from 'react'

export default function useDragScroll(
  sensitivity = 2,
  extraDependencies?: any
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(e.pageX - (containerRef.current?.offsetLeft || 0))
    setScrollLeft(containerRef.current?.scrollLeft || 0)
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    const x = e.pageX - (containerRef.current?.offsetLeft || 0)
    const walk = (x - startX) * sensitivity
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const onMouseUp = () => setIsDragging(false)
  const onMouseLeave = () => setIsDragging(false)

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(true)
    setStartX(touch.pageX - (containerRef.current?.offsetLeft || 0))
    setScrollLeft(containerRef.current?.scrollLeft || 0)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    e.preventDefault()
    const touch = e.touches[0]
    const x = touch.pageX - (containerRef.current?.offsetLeft || 0)
    const walk = (x - startX) * sensitivity
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const onMouseEnter = () => {
    const el = containerRef.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    el.dataset.hasWheel = 'true'

    el.addEventListener(
      'mouseleave',
      () => {
        if (el.dataset.hasWheel) {
          el.removeEventListener('wheel', onWheel)
          delete el.dataset.hasWheel
        }
      },
      { once: true }
    )
  }

  const onTouchEnd = () => setIsDragging(false)

  // Wheel scroll horizontal
  useEffect(() => {
    const el = containerRef.current

    if (!el) return

    const onWheel = (e: WheelEvent) => {
      // if user scroll vertical
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollTo({
          top: el.scrollTop + e.deltaY
        })
      }
      // if user scroll horizontal
      else {
        el.scrollTo({
          left: el.scrollLeft + e.deltaX
        })
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current, extraDependencies])

  return {
    containerRef,
    isDragging,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseEnter
  }
}
